/////////////////////////////////////////////////////////////////////////////////////////////
// Copyright 2026 Garmin International, Inc.
// Licensed under the Flexible and Interoperable Data Transfer (FIT) Protocol License; you
// may not use this file except in compliance with the Flexible and Interoperable Data
// Transfer (FIT) Protocol License.
/////////////////////////////////////////////////////////////////////////////////////////////
// ****WARNING****  This file is auto-generated!  Do NOT edit this file.
// Profile Version = 21.218.0Release
// Tag = production/release/21.218.0-0-g96fee6d7
/////////////////////////////////////////////////////////////////////////////////////////////

package com.garmin.fit

import com.garmin.fit.types.*
import kotlin.time.Instant

/**
 * The FIT `hsa_spo2_data` message.
 *
 * User's current SpO2 data used for HSA custom data logging
 *
 * A thin typed view over [Mesg]: the accessors below read and write the same
 * numbered fields the untyped API exposes, so nothing is hidden by using them.
 */
public open class HsaSpo2DataMesg : Mesg {
    public constructor() : super("HsaSpo2Data", Profile.MesgNum.HSA_SPO2_DATA)

    public constructor(mesg: Mesg) : super(mesg)

    internal constructor(mesg: Mesg, adopt: Boolean) : super(mesg, adopt)

    /** timestamp, in s. */
    public var timestamp: Instant?
        get() = (getFieldValue(TIMESTAMP_FIELD_NUM) as? UInt)?.let { FitDateTime.toInstant(it) }
        set(value) = setFieldValue(TIMESTAMP_FIELD_NUM, value?.let { FitDateTime.toTimestamp(it) })

    /** processing_interval, in s. Processing interval length in seconds */
    public var processingInterval: UShort?
        get() = getFieldValue(PROCESSING_INTERVAL_FIELD_NUM) as? UShort
        set(value) = setFieldValue(PROCESSING_INTERVAL_FIELD_NUM, value)

    /** Number of values stored in reading_spo2. */
    public val numReadingSpo2: Int
        get() = getField(READING_SPO2_FIELD_NUM)?.numValues ?: 0

    /** reading_spo2, in percent. SpO2 Reading: [70,100] Blank: 240 */
    public var readingSpo2: List<UByte?>?
        get() {
            val field = getField(READING_SPO2_FIELD_NUM) ?: return null
            return List(field.numValues) { getReadingSpo2(it) }
        }
        set(value) {
            removeField(READING_SPO2_FIELD_NUM)
            value?.forEachIndexed { index, element -> setReadingSpo2(index, element) }
        }

    public fun getReadingSpo2(index: Int): UByte? =
        getFieldValue(READING_SPO2_FIELD_NUM, index) as? UByte

    public fun setReadingSpo2(index: Int, value: UByte?) {
        setFieldValue(READING_SPO2_FIELD_NUM, value, index)
    }

    /** Number of values stored in confidence. */
    public val numConfidence: Int
        get() = getField(CONFIDENCE_FIELD_NUM)?.numValues ?: 0

    /** confidence. SpO2 Confidence: [0,254] */
    public var confidence: List<UByte?>?
        get() {
            val field = getField(CONFIDENCE_FIELD_NUM) ?: return null
            return List(field.numValues) { getConfidence(it) }
        }
        set(value) {
            removeField(CONFIDENCE_FIELD_NUM)
            value?.forEachIndexed { index, element -> setConfidence(index, element) }
        }

    public fun getConfidence(index: Int): UByte? =
        getFieldValue(CONFIDENCE_FIELD_NUM, index) as? UByte

    public fun setConfidence(index: Int, value: UByte?) {
        setFieldValue(CONFIDENCE_FIELD_NUM, value, index)
    }

    public companion object {
        public const val TIMESTAMP_FIELD_NUM: UByte = 253u
        public const val PROCESSING_INTERVAL_FIELD_NUM: UByte = 0u
        public const val READING_SPO2_FIELD_NUM: UByte = 1u
        public const val CONFIDENCE_FIELD_NUM: UByte = 2u

        /**
         * The profile definition of every field of this message, with no values.
         *
         * Built once when this class is first touched, and handed out only as
         * copies, so the decoder pays for a message type it never meets nothing.
         */
        internal val PROTOTYPE_FIELDS: Map<Int, Field> = buildPrototypeFields()

        private fun buildPrototypeFields(): Map<Int, Field> {
            val fields = LinkedHashMap<Int, Field>()
            fields[253] = Field("Timestamp", 253u, BaseType.UINT32, 1.0, 0.0, "s", false)
            fields[0] = Field("ProcessingInterval", 0u, BaseType.UINT16, 1.0, 0.0, "s", false)
            fields[1] = Field("ReadingSpo2", 1u, BaseType.UINT8, 1.0, 0.0, "percent", false)
            fields[2] = Field("Confidence", 2u, BaseType.UINT8, 1.0, 0.0, "", false)
            return fields
        }
    }
}
